import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import { Header, Spinner, Button, BackButton } from 'view/components';
import { PublicHeader } from 'view/components/PublicHeader';
import { useContextSelector as useCS } from 'use-context-selector';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import { storageService } from 'infrastructure/services/StorageService';
import { ChatSocketProvider } from 'App';
import * as CS from 'view/pages/candidates/commonStyled';
import { useGetCompanyBySlug } from 'view/hooks/useGetCompanyBySlug';
import { useGoBack } from 'view/hooks/useGoBack';
import { COMPANY_JOBS_PER_PAGE, useGetCompanyJobs } from 'view/hooks/useGetCompanyJobs';
import { RequestCompanyChangesModal } from './RequestCompanyChangesModal';
import * as S from './styled';

const buildLocationLabel = (company: any): string => {
  return [company?.city, company?.region, company?.country].filter(Boolean).join(', ');
};

const buildIndustryLabel = (company: any): string => {
  return company?.industryRaw || '';
};

export const CompanyProfile: FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack('/companies');
  const [page, setPage] = useState(1);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [changeModalOpen, setChangeModalOpen] = useState(false);

  const { isAuthenticated } = useAuth();
  const userData = useCS(GlobalConfigContext, globalConfigSelectors.userData);
  const isRecruiter = userData?.role?.name === RolesEnum.RECRUITER;
  const { getCompanyBySlug, data: company, isLoading, error } = useGetCompanyBySlug();
  const { getCompanyJobs, data: jobsData, isLoading: isLoadingJobs } = useGetCompanyJobs();

  useEffect(() => {
    storageService.getItem('accessToken').then(setToken);
  }, []);

  useEffect(() => {
    if (!slug) return;
    setPage(1);
    getCompanyBySlug(slug);
    getCompanyJobs(slug);
  }, [slug]);

  const renderHeader = () => {
    if (isAuthenticated) {
      return (
        <ChatSocketProvider token={token}>
          <Header />
        </ChatSocketProvider>
      );
    }
    return <PublicHeader />;
  };

  const renderContent = () => {
    if (error) {
      return <S.ErrorText>{error?.response?.data?.message || 'Company not found'}</S.ErrorText>;
    }

    if (isLoading || !company) {
      return (
        <S.CenteredLoader>
          <Spinner variant="outlined" />
        </S.CenteredLoader>
      );
    }

    const companyLocation = buildLocationLabel(company);
    const companyIndustry = buildIndustryLabel(company);
    const monogram = company.name?.charAt(0)?.toUpperCase() || '?';
    const jobs = jobsData?.data || [];
    const jobsTotal = jobsData?.total || 0;

    return (
      <>
        {/* Company header */}
        <S.Card>
          <S.CardTopRow>
            <S.CompanyHeaderRow>
              <S.Logo
                src={company.effectiveLogoUrl || undefined}
                alt={company.name}
                variant="rounded"
                hasImage={!!company.effectiveLogoUrl}
              >
                {monogram}
              </S.Logo>

              <S.HeaderInfo>
                <S.CompanyName>{company.name}</S.CompanyName>

                {company.descriptionShort && (
                  <S.ShortDescription>{company.descriptionShort}</S.ShortDescription>
                )}

                <S.MetaRow>
                  {companyIndustry && <S.Tag>{companyIndustry}</S.Tag>}

                  {companyLocation && (
                    <S.MetaItem>
                      <LocationOnIcon fontSize="small" />
                      {companyLocation}
                    </S.MetaItem>
                  )}

                  {company.websiteUrl && (
                    <S.WebsiteLink
                      href={company.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LanguageIcon fontSize="small" />
                      Website
                    </S.WebsiteLink>
                  )}
                </S.MetaRow>
              </S.HeaderInfo>
            </S.CompanyHeaderRow>

            {isRecruiter && (
              <>
                <S.EditButtonDesktop>
                  <Button
                    variant="outlined"
                    text="Edit"
                    onClick={() => {
                      setChangeModalOpen(true);
                    }}
                    startIcon={<EditIcon fontSize="small" />}
                    style={{ minHeight: '36px', fontSize: '14px', width: 'auto' }}
                  />
                </S.EditButtonDesktop>
                <S.EditButtonMobile
                  onClick={() => {
                    setChangeModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </S.EditButtonMobile>
              </>
            )}
          </S.CardTopRow>

          {/* About — divided section inside the same card (like CandidateProfile) */}
          {company.descriptionLong && (
            <S.AboutSection>
              <S.SectionTitle>About {company.name}</S.SectionTitle>
              {company.descriptionLong
                .split(/\n+/)
                .filter((paragraph: string) => {
                  return paragraph.trim();
                })
                .map((paragraph: string) => {
                  return <S.AboutText key={paragraph}>{paragraph}</S.AboutText>;
                })}
            </S.AboutSection>
          )}
        </S.Card>

        {/* Open positions */}
        <S.Card>
          <S.SectionTitle>
            <WorkIcon fontSize="small" />
            Open Positions ({jobsTotal})
          </S.SectionTitle>

          {jobs.length > 0 ? (
            <>
              <S.JobList>
                {jobs.map((job: any) => {
                  return (
                    <S.JobCard
                      key={job.id}
                      onClick={() => {
                        navigate(`/jobs/${job.id}`);
                      }}
                    >
                      <S.JobTitle>{job.jobTitle}</S.JobTitle>

                      <S.JobMeta>
                        {job.location && (
                          <S.MetaItem>
                            <LocationOnIcon fontSize="small" />
                            {job.location.name}
                          </S.MetaItem>
                        )}

                        {job.positionType?.length > 0 &&
                          job.positionType.map((type: any) => {
                            return <S.Tag key={type.id}>{type.name}</S.Tag>;
                          })}
                      </S.JobMeta>

                      {job.salaryMin > 0 && (
                        <S.JobSalary>
                          Salary: ${job.salaryMin.toLocaleString()} - $
                          {job.salaryMax.toLocaleString()}
                        </S.JobSalary>
                      )}

                      {job.rolesAndResponsibilities && (
                        <S.JobDescription>{job.rolesAndResponsibilities}</S.JobDescription>
                      )}
                    </S.JobCard>
                  );
                })}
              </S.JobList>

              <CS.CustomPagination
                count={Math.ceil(jobsTotal / COMPANY_JOBS_PER_PAGE)}
                shape="rounded"
                page={page}
                onChange={(e, value) => {
                  setPage(value);
                  getCompanyJobs(slug, value);
                }}
                style={{ margin: '24px auto 0' }}
              />
            </>
          ) : (
            !isLoadingJobs && (
              <S.EmptyText>No open positions at the moment. Check back later!</S.EmptyText>
            )
          )}
        </S.Card>

        <S.Attribution>Company data from People Data Labs</S.Attribution>

        <RequestCompanyChangesModal
          open={changeModalOpen}
          onClose={() => {
            setChangeModalOpen(false);
          }}
          company={company}
        />
      </>
    );
  };

  return (
    <S.Wrapper>
      {renderHeader()}
      <S.MainContentWrapper>
        <S.BackButtonBox>
          <BackButton text="Back" onClick={goBack} />
        </S.BackButtonBox>
        {renderContent()}
      </S.MainContentWrapper>
    </S.Wrapper>
  );
};
